import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ChatCircleText,
  EnvelopeSimple,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const INITIAL_FORM = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

export default function Soporte() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading) return;

    setLoading(true);

    try {
      await api.post("/support/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });

      setSent(true);
      setForm(INITIAL_FORM);
      toast.success("Tu consulta se ha enviado correctamente.");
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        "No hemos podido enviar tu consulta. Inténtalo de nuevo más tarde.";

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const inputClassName =
    "w-full rounded-xl border border-med-border bg-med-bg px-4 py-3 text-med-text placeholder:text-med-muted focus:border-med-primary focus:outline-none focus:ring-2 focus:ring-med-primary/20 disabled:opacity-60";

  return (
    <main className="min-h-screen bg-med-bg px-4 py-8 md:px-8 md:py-12">
      <article className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-med-border bg-med-surface shadow-sm">
        <div className="border-b border-med-border p-6 md:p-10">
          <Link
            to="/login"
            className="mb-8 inline-flex items-center gap-2 text-sm text-med-muted transition-colors hover:text-med-text"
          >
            <ArrowLeft size={18} />
            Volver al inicio de sesión
          </Link>

          <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-med-primary/10 text-med-primary">
            <ChatCircleText size={30} weight="duotone" />
          </div>

          <h1 className="text-3xl font-bold text-med-text">
            Centro de soporte
          </h1>

          <p className="mt-3 max-w-2xl leading-relaxed text-med-muted">
            ¿Tienes alguna duda o necesitas ayuda con Trakka? Cuéntanos qué
            ocurre y nuestro equipo revisará tu consulta.
          </p>
        </div>

        <div className="p-6 md:p-10">
          {sent ? (
            <div
              role="status"
              className="rounded-2xl border border-med-border bg-med-bg p-6 text-center"
            >
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-med-primary/10 text-med-primary">
                <EnvelopeSimple size={28} weight="duotone" />
              </div>

              <h2 className="text-xl font-semibold text-med-text">
                ¡Consulta enviada!
              </h2>

              <p className="mt-2 text-med-muted">
                Hemos recibido tu mensaje. Gracias por contactar con el equipo
                de Trakka.
              </p>

              <button
                type="button"
                onClick={() => setSent(false)}
                className="mt-6 rounded-xl bg-med-primary px-5 py-3 font-medium text-white transition-opacity hover:opacity-90"
              >
                Enviar otra consulta
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="support-name"
                  className="mb-2 block text-sm font-medium text-med-text"
                >
                  Nombre
                </label>
                <input
                  id="support-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  maxLength={100}
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Tu nombre"
                  className={inputClassName}
                  disabled={loading}
                />
              </div>

              <div>
                <label
                  htmlFor="support-email"
                  className="mb-2 block text-sm font-medium text-med-text"
                >
                  Correo electrónico
                </label>
                <input
                  id="support-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  maxLength={254}
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="tu@correo.com"
                  className={inputClassName}
                  disabled={loading}
                />
              </div>

              <div>
                <label
                  htmlFor="support-subject"
                  className="mb-2 block text-sm font-medium text-med-text"
                >
                  Asunto
                </label>
                <input
                  id="support-subject"
                  name="subject"
                  type="text"
                  maxLength={150}
                  minLength={3}
                  required
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="¿En qué podemos ayudarte?"
                  className={inputClassName}
                  disabled={loading}
                />
              </div>

              <div>
                <label
                  htmlFor="support-message"
                  className="mb-2 block text-sm font-medium text-med-text"
                >
                  Mensaje
                </label>
                <textarea
                  id="support-message"
                  name="message"
                  rows={6}
                  maxLength={5000}
                  minLength={10}
                  required
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Describe tu consulta con el mayor detalle posible..."
                  className={`${inputClassName} resize-y`}
                  disabled={loading}
                />
                <p className="mt-2 text-right text-xs text-med-muted">
                  {form.message.length}/5000 caracteres
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-med-primary px-5 py-3.5 font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <PaperPlaneTilt size={20} weight="bold" />
                {loading ? "Enviando consulta..." : "Enviar consulta"}
              </button>

              <p className="text-center text-xs leading-relaxed text-med-muted">
                Utilizaremos tus datos únicamente para gestionar y responder a
                tu consulta de soporte.
              </p>
            </form>
          )}
        </div>
      </article>

      <p className="mt-6 text-center text-sm text-med-muted">
        ¿Quieres conocer cómo tratamos tus datos?{" "}
        <Link
          to="/politica-privacidad"
          className="font-medium text-med-primary hover:underline"
        >
          Consulta nuestra política de privacidad
        </Link>
        .
      </p>
    </main>
  );
}
